import { useMemo } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $profileFeatureStore } from '@lib/store';
import { useCardBalance, useCurrencySymbol } from '@shared/hooks';
import { FormatUtils, AmountUtils, PaletteUtils, cn } from '@shared/utils';
import { CardKing, type CardsResponse, CardStatus } from '@shared/api/rest/cards';
import { Icon, Text } from '@shared/widgets';
import VisaIcon from '@assets/icons/card/visa.svg';
import MastercardIcon from '@assets/icons/card/mastercard.svg';
import PinstripeIcon from '@assets/icons/card/pinstripe.svg';
import styles from './cardItem.module.css';

interface CardItemProps {
  className?: string;
  card: CardsResponse;
  withHover?: boolean;
}

export const CardItem = (props: CardItemProps) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);

  const { t } = useTranslation('accounts');

  const symbol = useCurrencySymbol(props.card.currency);
  const balance = useCardBalance(props.card.cardId);

  const viewCardDetails = useMemo(
    () => profileFeatureStore.data?.cardFlows?.find((feature) => feature.cardId === props.card.cardId)?.viewCardDetails,
    [profileFeatureStore.data, props.card],
  );
  return (
    <div
      className={cn('group relative', styles['cards-grid__col'], viewCardDetails === false && 'cursor-not-allowed', props.className)}
      title={viewCardDetails === false ? t('card.viewCardDetails') : undefined}
    >
      <div
        className={cn(
          styles.card,
          PaletteUtils.isInsigniaPalette && styles.card,
          PaletteUtils.isVcfcPalette && 'relative flex h-[103px] w-[176px] flex-col rounded-lg bg-primary p-3 text-ice',
          {
            [styles['card-closed']]: props.card.status === CardStatus.CLOSED,
            [styles['card-disabled']]: props.card.status === CardStatus.DELINQUENT || props.card.status === CardStatus.INACTIVE,
            [styles['card-blocked']]: props.card.status === CardStatus.BLOCKED || props.card.status === CardStatus.FROZEN,
          },
          PaletteUtils.isVcfcPalette && [
            (props.card.status === CardStatus.BLOCKED || props.card.status === CardStatus.DELINQUENT) && 'bg-soft-black',
            props.card.status === CardStatus.CLOSED && 'bg-slate',
            props.card.status === CardStatus.INACTIVE && '!bg-light-gray',
          ],
        )}
      >
        {PaletteUtils.isInsigniaPalette && (
          <>
            <svg
              className={cn(styles['card__ellipse'], styles['card__ellipse_1'])}
              width='122'
              height='87'
              fill='transparent'
              viewBox='0 0 122 87'
              xmlns='http://www.w3.org/2000/svg'
            >
              <ellipse cx='61' cy='43.5' rx='60' ry='42.5' />
            </svg>
            <svg
              className={cn(styles['card__ellipse'], styles['card__ellipse_2'])}
              width='122'
              height='87'
              fill='transparent'
              viewBox='0 0 122 87'
              xmlns='http://www.w3.org/2000/svg'
            >
              <ellipse cx='61' cy='43.5' rx='60' ry='42.5' />
            </svg>
          </>
        )}
        {PaletteUtils.isVcfcPalette && <Icon icon={<PinstripeIcon />} className='absolute top-0' />}

        <ul className={styles['card__info']}>
          <li>
            <span className={styles['card__info-item']}>{t(`card.type.${props.card.cardType.toLowerCase()}`)}</span>
          </li>
          <li>
            <span className={cn(styles['card__info-item'], styles['card__status'])}>{t(`statuses.card.${props.card.status}`, { ns: 'common' })}</span>
          </li>
        </ul>
        <div className={styles['card__amount']}>
          {symbol}
          {typeof balance === 'number' ? AmountUtils.transformToAmountWithCurrency(balance as number) : ''}
        </div>
        <div className={styles['card__bottom']}>
          <div className={styles['left']}>
            <div className={styles['card__number']}>{FormatUtils.toSliceNumber(props.card.cardNumber, 16)}</div>
            <div className={styles['card__name']}>{props.card.cardHolder}</div>
          </div>
          {props.card.cardKind === CardKing.Credit && <Icon icon={<VisaIcon />} color='white' />}
          {props.card.cardKind === CardKing.Debit && <Icon icon={<MastercardIcon className='h-[27.5px] w-full' />} />}
        </div>
      </div>
      {props.withHover && (
        <Text
          className={cn(
            'absolute left-1/2 top-full -translate-x-1/2 whitespace-nowrap text-center opacity-0 transition-opacity duration-200',
            'group-hover:opacity-100',
          )}
          size='sm'
        >
          Click to view details
        </Text>
      )}
    </div>
  );
};
